import { displayFonts, monoFont, sansFonts, type FontOption } from '@/content/fonts'
import { dtcgModes, penpotTokens } from '@/design-tools/tokens'
import { swatches } from '@/design-tools/files'
import { themeFor, type Mission } from '@/content/missions'
import { deriveTheme, roleDeclarations, type ThemePair } from '@/content/theme'
import { titleCase } from '@/content/casing'
import { SITE_URL, SYSTEM_VERSION } from '@/content/system'
import { defaultDurations, durationTokens, type Prefs } from '@/state/prefs'

export type KitSources = {
  /** Core tokens, as published at /mds/tokens.css. */
  tokens: string
  /** Component styles, as published at /mds/components.css. */
  components: string
  /** The latin woff2 file for each chosen typeface, by font id. */
  fonts: Record<string, Uint8Array>
}

type Brand = { name: string; place: string }

/**
 * The kit's name: the site's name, the mode, the headline face where it is
 * not the default, "custom" for edited colors, and the date as YYYYMMDD, so
 * downloads of different designs, or on different days, stay distinct.
 */
export function kitName(prefs: Prefs, brand: Brand, date = new Date()) {
  const stamp = [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((n) => String(n).padStart(2, '0')).join('')
  return [brand.name, prefs.theme, prefs.fontDisplay === displayFonts[0].id ? '' : prefs.fontDisplay, prefs.palette ? 'custom' : '', stamp]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
}

function family(font: FontOption) {
  return font.specimen.split('/specimen/')[1].replace(/\+/g, ' ')
}

function fontFace(font: FontOption) {
  const variable = font.package.includes('-variable/')
  return `@font-face {
  font-family: "${family(font)}";
  font-style: normal;
  font-display: swap;
  font-weight: ${variable ? '100 900' : '400'};
  src: url("../fonts/${font.id}.woff2") format("woff2");
}
`
}

/** The kit's theme: the chosen typefaces, bundled, and the active roles. */
function themeCss(theme: ThemePair, display: FontOption, sans: FontOption, label: string, durations: number[]) {
  return `/* ${label}. Exported from Mission Design System ${SYSTEM_VERSION}. */
${[display, sans, monoFont].map(fontFace).join('\n')}
:root {
  color-scheme: light;
${roleDeclarations(theme.light)}
  --mds-font-display: "${family(display)}", ${display.stack};
  --mds-font-display-scale: ${display.scale ?? 1};
  --mds-font-sans: "${family(sans)}", ${sans.stack};
  --mds-font-mono: "${family(monoFont)}", ${monoFont.stack};
${durationTokens.map((name, i) => `  --mds-duration-${name}: ${durations[i]}ms;`).join('\n')}
}

[data-theme="dark"] {
  color-scheme: dark;
${roleDeclarations(theme.dark)}
}
`
}

/** Text set into the sample page's markup, such as "Science & Technology". */
const escapeHtml = (text: string) => text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')

function sampleHtml(prefs: Prefs, brand: Brand) {
  const t = (text: string) => (prefs.casing === 'title' ? titleCase(text) : text)
  return `<!doctype html>
<html lang="en" data-theme="${prefs.theme}" data-radius="${prefs.radius}" data-density="${prefs.density}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escapeHtml(brand.name)}</title>
    <link rel="stylesheet" href="css/tokens.css" />
    <link rel="stylesheet" href="css/theme.css" />
    <link rel="stylesheet" href="css/components.css" />
  </head>
  <body>
    <main class="mds-container" style="display: grid; gap: 2rem; padding-block: 4rem">
      <span class="mds-eyebrow">${escapeHtml(brand.name)}</span>
      <h1 class="mds-hero-title">Design for ${escapeHtml(brand.place)}.</h1>
      <p class="mds-lead">${t('Every component below reads the roles in css/theme.css.')}</p>
      <p class="mds-cluster">
        <a class="mds-button mds-button--primary" href="#">${t('Start building')}</a>
        <a class="mds-button mds-button--secondary" href="#">${t('Explore the components')}</a>
        <a class="mds-button mds-button--accent" href="#">${t('Accent action')}</a>
        <span class="mds-tag mds-tag--accent">${t('New')}</span>
      </p>
      <div class="mds-alert mds-alert--success" role="status">
        <p class="mds-alert__title">${t('Request received')}</p>
        <p>We will reply within two business days.</p>
      </div>
      <article class="mds-card mds-card--raised">
        <h2 class="mds-card__title">${t('Apply for a permit')}</h2>
        <p>Start an application, save it, and come back to finish later.</p>
      </article>
      <div class="mds-field">
        <label class="mds-label" for="email">${t('Email address')}</label>
        <span class="mds-hint">We only use this to send updates about your request.</span>
        <input class="mds-input" id="email" type="email" autocomplete="email" />
      </div>
    </main>
  </body>
</html>
`
}

function readme(prefs: Prefs, brand: Brand, mission: Mission, display: FontOption, sans: FontOption, colors: string[]) {
  const rows = [
    ['Mission', mission.name],
    ['Colors', prefs.palette ? `Custom: ${colors.join(', ')}` : `${mission.short} palette: ${colors.join(', ')}`],
    ['Default theme', prefs.theme],
    ['Headline font', `${display.name} (${display.package})`],
    ['Body font', `${sans.name} (${sans.package})`],
    ['Shape', prefs.radius],
    ['Density', prefs.density],
    ['Case', prefs.casing === 'title' ? 'Title Case' : 'Sentence case'],
  ]
  return `# ${brand.name} design kit

Exported from Mission Design System ${SYSTEM_VERSION} (${SITE_URL}).

## Choices

| Setting | Value |
|---|---|
${rows.map(([key, value]) => `| ${key} | ${value} |`).join('\n')}

## Files

| File | Contents |
|---|---|
| \`index.html\` | A sample page using the kit |
| \`css/tokens.css\` | Core tokens: type, space, shape, motion |
| \`css/theme.css\` | Every color role for light and dark, and the chosen typefaces |
| \`css/components.css\` | Component styles (\`mds-*\` classes) |
| \`fonts/\` | The typefaces, as woff2 files under their open licenses |
| \`design.json\` | The same choices and roles, for tools and agents |
| \`design-tools/figma/\` | Figma variables: drop \`light.tokens.json\` and \`dark.tokens.json\` on a new collection in the Variables panel, and each becomes a mode |
| \`design-tools/penpot.tokens.json\` | Penpot tokens: import from the Tokens panel |
| \`design-tools/swatches.ase\` | Adobe swatches for Illustrator, Photoshop and InDesign: Swatches panel, Open Swatch Library, Other Library |

## Use

Link the three stylesheets in this order, then set the theme on \`<html>\`:

\`\`\`html
<link rel="stylesheet" href="css/tokens.css" />
<link rel="stylesheet" href="css/theme.css" />
<link rel="stylesheet" href="css/components.css" />

<html data-theme="${prefs.theme}" data-radius="${prefs.radius}" data-density="${prefs.density}">
\`\`\`

The typefaces are bundled in \`fonts/\` and declared in \`css/theme.css\`, so the
kit works offline. They come from Google Fonts through Fontsource:
${display.package}, ${sans.package} and ${monoFont.package}.

Components and patterns are documented at ${SITE_URL}/components.
`
}

/** Every file in the design kit, keyed by its path inside the zip. */
export function designKitFiles(
  prefs: Prefs,
  mission: Mission,
  brand: Brand,
  sources: KitSources,
  folder = kitName(prefs, brand),
): Record<string, string | Uint8Array> {
  const display = displayFonts.find((font) => font.id === prefs.fontDisplay) ?? displayFonts[0]
  const sans = sansFonts.find((font) => font.id === prefs.fontSans) ?? sansFonts[0]
  const custom = prefs.palette ? prefs.palette.split(',') : null
  const theme = custom ? deriveTheme({ primary: custom[0], secondary: custom[1], accent: custom[2] }) : themeFor(mission)
  const colors = custom ?? [mission.palette.primary, mission.palette.secondary, mission.palette.accent]
  // Edited motion durations travel with the kit, as the site applies them.
  const durations = prefs.durations ? prefs.durations.split(',').map(Number) : defaultDurations
  const design = {
    name: brand.name,
    version: SYSTEM_VERSION,
    mission: mission.id,
    palette: { primary: colors[0], secondary: colors[1], accent: colors[2] },
    custom: Boolean(custom),
    theme: prefs.theme,
    fonts: { display: display.id, sans: sans.id, mono: monoFont.id },
    radius: prefs.radius,
    density: prefs.density,
    casing: prefs.casing,
    durations: Object.fromEntries(durationTokens.map((name, i) => [name, durations[i]])),
    roles: theme,
  }
  const figma = dtcgModes(theme, brand.name)
  return {
    [`${folder}/README.md`]: readme(prefs, brand, mission, display, sans, colors),
    [`${folder}/index.html`]: sampleHtml(prefs, brand),
    [`${folder}/css/tokens.css`]: sources.tokens,
    [`${folder}/css/theme.css`]: themeCss(theme, display, sans, `${brand.name} theme`, durations),
    [`${folder}/css/components.css`]: sources.components,
    [`${folder}/design.json`]: `${JSON.stringify(design, null, 2)}\n`,
    [`${folder}/design-tools/figma/light.tokens.json`]: figma.light,
    [`${folder}/design-tools/figma/dark.tokens.json`]: figma.dark,
    [`${folder}/design-tools/penpot.tokens.json`]: penpotTokens(theme, brand.name),
    [`${folder}/design-tools/swatches.ase`]: swatches(theme, brand.name),
    ...Object.fromEntries(
      [display, sans, monoFont]
        .filter((font) => sources.fonts[font.id])
        .map((font) => [`${folder}/fonts/${font.id}.woff2`, sources.fonts[font.id]]),
    ),
  }
}
